import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';

import { GdprService } from '@recruitee/gdpr/data';
import { EmailTemplate, EmailTemplatesEventInvitation } from '@recruitee/http/types';
import { ConnectedOverlayDirective, KeyCode, RtStatefulComponent } from '@recruitee/user-interface';
import { asViewRef } from '@recruitee/utils';

import { RedactorInsertsStep } from '../../../redactor/types';
import { WysiwygLink } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { InsertsEmailKind } from '../redactor-inserts-email-templates/redactor-inserts-email-templates.component';

type State = {
  step: RedactorInsertsStep;
  selectedIndex: number | null;
  availableInserts: RedactorInsertsStep[];
};

@Component({
  selector: 'rt-redactor-inserts-dropdown',
  templateUrl: 'redactor-inserts-dropdown.component.html',
  styleUrls: ['./redactor-inserts-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:keydown)': 'handleKeyDown($event)',
  },
})
export class RedactorInsertsDropdownComponent extends RtStatefulComponent<State> {
  public readonly InsertsEmailKind = InsertsEmailKind;

  @Input() public inserts: RedactorInsertsStep[];
  @Input() public emailTemplateInsideTemplate?: TemplateRef<any>;
  @Input() public dropdownRef: ConnectedOverlayDirective;
  @Input() public canManageCandidates: boolean;

  @Output() public emailTemplateChoose: EventEmitter<
    EmailTemplate | EmailTemplatesEventInvitation
  > = new EventEmitter();
  @Output() public insertLink: EventEmitter<WysiwygLink> = new EventEmitter();
  @Output() public saveAsTemplate = new EventEmitter<void>();

  constructor(
    protected cdRef: ChangeDetectorRef,
    public gdprSvc: GdprService,
    private router: Router,
  ) {
    super(cdRef);
    this.createState({
      step: 'insertion-list',
      selectedIndex: null,
      availableInserts: [],
    });
  }

  public ngOnChanges({ inserts }: SimpleChanges): void {
    if (inserts && inserts.currentValue) {
      let availableInserts = inserts.currentValue;

      if (!this.gdprSvc.gdprSettingEnabled || !this.canManageCandidates) {
        availableInserts = inserts.currentValue.filter(
          (i: RedactorInsertsStep) => i !== 'gdpr-links',
        );
      }

      if (availableInserts && availableInserts.length === 1) {
        this.setState({ ...this.state, availableInserts, step: inserts.currentValue[0] });
      } else {
        this.setState({ ...this.state, availableInserts });
      }
    }
  }

  public isStep(insertName: RedactorInsertsStep): boolean {
    return this.state.step === insertName;
  }

  public hasStep(insertName: RedactorInsertsStep): boolean {
    return this.state.availableInserts.includes(insertName);
  }

  public hasBack(): boolean {
    return this.state.availableInserts.length !== 1;
  }

  public closeDropdown(): void {
    this.dropdownRef.open = false;
  }

  public goToSettings(url: string): void {
    this.router.navigate([url]);
  }

  public setStep(step: RedactorInsertsStep): void {
    this.setState({ ...this.state, step, selectedIndex: null });
  }

  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    if (!this.isStep('insertion-list') && key !== KeyCode.Escape) {
      return;
    }
    switch (key) {
      case KeyCode.Escape:
        this.closeDropdown();
        break;

      case KeyCode.Enter:
        event.preventDefault();
        event.stopPropagation();
        if (this.state.selectedIndex === null) {
          this.closeDropdown();
        } else {
          this.setStep(this.state.availableInserts[this.state.selectedIndex]);
        }
        break;

      case KeyCode.UpArrow:
        event.preventDefault();
        if (this.state.selectedIndex === 0 || this.state.selectedIndex === null) {
          this.setState({ ...this.state, selectedIndex: this.state.availableInserts.length - 1 });
        } else {
          this.setState({ ...this.state, selectedIndex: this.state.selectedIndex - 1 });
        }
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        if (
          this.state.selectedIndex === this.state.availableInserts.length - 1 ||
          this.state.selectedIndex === null
        ) {
          this.setState({ ...this.state, selectedIndex: 0 });
        } else {
          this.setState({ ...this.state, selectedIndex: this.state.selectedIndex + 1 });
        }
        break;

      default:
        return;
    }
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  public isSelected(insert: RedactorInsertsStep): boolean {
    return this.state.availableInserts.indexOf(insert) === this.state.selectedIndex;
  }

  public trackByInsert(index: number, insert: RedactorInsertsStep): string {
    return insert;
  }
}
